import { useEffect, useRef, useState } from 'react';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmEventDTO } from '../../../server/dto/crm-event-dto.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { RichTextForm } from '../../rich-text/components/rich-text-form.tsx';
import type { RichTextValue } from '../../rich-text/components/rich-text-input.tsx';
import { RichTextPlaceholder } from '../../rich-text/components/rich-text-placeholder.tsx';

type EventNoteCreateFormProps = {
    /**
     * Right now the event customerId is not required,
     * but we need it to create note, so we require it from props.
     */
    event: CrmEventDTO & { customerId: string };
    occurrenceDate: string;
    onCancel: () => void;
    onSuccess?: () => void;
};

export const EventNoteCreateForm = ({
    event,
    occurrenceDate,
    onCancel,
    onSuccess,
}: EventNoteCreateFormProps) => {
    const { t } = useLocale('events');
    const utils = api.useUtils();
    const sendEvent = useAnalytics();

    const createMutation = api.platoUser.command.notes.create.useMutation({
        onSuccess: () => {
            void utils.platoUser.query.events.getEventById.invalidate({ eventId: event.event_id });
            void utils.platoUser.query.events.getUpcomingCustomerEvents.invalidate({
                customerId: event.customerId,
            });
            void utils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate({
                customerId: event.customerId,
            });
        },
    });

    return (
        <RichTextForm
            autoFocus
            placeholder={t('note_placeholder')}
            submitTitle={t('note_submit_button')}
            entityType="note"
            isSubmitting={createMutation.isLoading}
            onCancel={onCancel}
            onSubmit={(content: RichTextValue, aiSessionId?: string) => {
                createMutation.mutate(
                    {
                        title: content,
                        customerId: event.customerId,
                        eventId: event.event_id,
                        occurredAt: new Date(
                            `${occurrenceDate}T${event.start_time || '12:00:00'}`,
                        ).toISOString(),
                        noteType: 'VISIT',
                    },
                    {
                        onSuccess: (note) => {
                            sendEvent('customer_interaction_created', {
                                note_id: note.noteId,
                                customer_id: note.customerId,
                                note_type: note.noteType,
                                ai_session_id: aiSessionId,
                                page_source: 'event_drawer',
                            });
                            onSuccess?.();
                        },
                    },
                );
            }}
        />
    );
};

export const EventNoteCreate = (props: Omit<EventNoteCreateFormProps, 'onCancel'>) => {
    const { t } = useLocale('events');
    const [isAddingNote, setIsAddingNote] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isAddingNote) {
            containerRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isAddingNote]);

    // Separating this two component to avoid unnecessary AI sessions to spawn
    return (
        <div ref={containerRef}>
            {isAddingNote ? (
                <EventNoteCreateForm
                    onCancel={() => {
                        setIsAddingNote(false);
                    }}
                    {...props}
                />
            ) : (
                <RichTextPlaceholder
                    onClick={() => {
                        setIsAddingNote(true);
                    }}
                    placeholderText={t('note_placeholder')}
                />
            )}
        </div>
    );
};
